import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Clock, MapPin } from 'lucide-react';
import { SIGNATURE_FILM } from '@/lib/data';
import { VideoModal } from './VideoModal';

const MORE_WORK = [
    { src: '/portfolio/p3_brides.webp', caption: 'The Procession' },
    { src: '/portfolio/p5_waterfall.webp', caption: 'The Ritual' },
    { src: '/portfolio/p4_mountain.webp', caption: 'Something More' },
    { src: '/portfolio/p2_window.webp', caption: 'The Embrace' },
    { src: '/portfolio/p1_ritual.webp', caption: 'The Mountain Vow' },
];

export const FilmShowcase = () => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <section
                id="films"
                data-testid="film-showcase-section"
                className="relative py-24 md:py-32 bg-ink"
            >
                <div className="px-6 md:px-12 lg:px-20">
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 md:mb-20">
                        <div className="max-w-3xl">
                            <p className="text-[11px] uppercase tracking-[0.45em] text-gold mb-5">
                                Signature Film · 2026
                            </p>
                            <motion.h2
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-100px' }}
                                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                                className="font-serif text-bone text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-light leading-[1.02]"
                            >
                                {SIGNATURE_FILM.title.split(' & ')[0]}
                                {SIGNATURE_FILM.title.includes(' & ') && (
                                    <>
                                        <span className="italic text-gold"> & </span>
                                        {SIGNATURE_FILM.title.split(' & ')[1]}
                                    </>
                                )}
                            </motion.h2>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.2 }}
                            className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.3em] text-bone-dim lg:text-right"
                        >
                            <span className="flex items-center gap-2 lg:justify-end">
                                <MapPin size={12} className="text-gold" /> {SIGNATURE_FILM.location}
                            </span>
                            <span className="flex items-center gap-2 lg:justify-end">
                                <Clock size={12} className="text-gold" /> {SIGNATURE_FILM.runtime}
                            </span>
                        </motion.div>
                    </div>

                    {/* Bento: Big poster (left) + 2x2 stills (right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:gap-4 auto-rows-[minmax(140px,auto)]">
                        {/* Primary poster with play */}
                        <motion.button
                            onClick={() => setOpen(true)}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="film-main-play"
                            className="group relative lg:col-span-8 lg:row-span-2 aspect-[16/10] lg:aspect-auto overflow-hidden cursor-pointer text-left"
                        >
                            <img
                                src={SIGNATURE_FILM.poster}
                                alt={SIGNATURE_FILM.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-ink/80 via-ink/20 to-transparent" />
                            <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors duration-500" />

                            {/* Chapter badge */}
                            <div className="absolute top-6 left-6 z-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                                <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" />
                                {SIGNATURE_FILM.chapter}
                            </div>

                            {/* Center play */}
                            <div className="absolute inset-0 flex items-center justify-center z-10">
                                <div className="relative">
                                    <span className="absolute inset-0 rounded-full border border-bone/40 scale-150 group-hover:scale-[1.9] transition-transform duration-700 opacity-60" />
                                    <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-bone/95 backdrop-blur-sm flex items-center justify-center group-hover:bg-gold transition-colors duration-300 shadow-2xl shadow-black/50">
                                        <Play size={22} className="fill-ink text-ink ml-1" />
                                    </span>
                                </div>
                            </div>

                            {/* Bottom caption */}
                            <div className="absolute bottom-0 inset-x-0 p-7 md:p-9 z-10">
                                <h3 className="font-serif text-bone text-3xl md:text-5xl tracking-tighter font-light leading-[1.05]">
                                    {SIGNATURE_FILM.title}
                                </h3>
                                <p className="mt-3 text-bone-dim text-sm md:text-base font-light max-w-xl leading-relaxed">
                                    {SIGNATURE_FILM.synopsis}
                                </p>
                                <p className="mt-5 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-bone group-hover:text-gold transition-colors duration-300">
                                    Play Signature Film
                                    <span className="w-10 h-px bg-current" />
                                </p>
                            </div>
                        </motion.button>

                        {/* Still thumbnails bento */}
                        {SIGNATURE_FILM.stills.slice(0, 4).map((s, i) => (
                            <motion.figure
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-80px' }}
                                transition={{ duration: 0.9, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                data-testid={`film-still-${i}`}
                                className={`relative overflow-hidden group ${
                                    i === 0 || i === 3 ? 'lg:col-span-2' : 'lg:col-span-2'
                                } aspect-[4/3]`}
                            >
                                <img
                                    src={s.src}
                                    alt={s.caption}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
                                <figcaption className="absolute bottom-0 inset-x-0 p-4 text-[10px] uppercase tracking-[0.3em] text-bone-dim group-hover:text-gold transition-colors">
                                    {s.caption}
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    {/* Secondary stills row — real wedding stories in full colour */}
                    <div className="mt-10 md:mt-14">
                        <p className="text-[11px] uppercase tracking-[0.4em] text-gold mb-5">
                            More Weddings, More Connection
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                            {MORE_WORK.map((s, i) => (
                                <motion.figure
                                    key={`strip-${i}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.9, delay: i * 0.08 }}
                                    data-testid={`more-work-${i}`}
                                    className={`relative overflow-hidden group ${
                                        i === 0 ? 'md:col-span-2 aspect-[16/10]' : 'aspect-[4/3]'
                                    }`}
                                >
                                    <img
                                        src={s.src}
                                        alt={s.caption}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-500" />
                                    <figcaption className="absolute bottom-0 inset-x-0 p-5 text-[11px] uppercase tracking-[0.3em] text-bone group-hover:text-gold transition-colors font-light">
                                        {s.caption}
                                    </figcaption>
                                </motion.figure>
                            ))}
                        </div>
                    </div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="mt-10 text-[11px] uppercase tracking-[0.4em] text-bone-dim text-center"
                    >
                        Stills pulled from the final cut · Shot by Bros On Board
                    </motion.p>
                </div>
            </section>

            <VideoModal
                open={open}
                onClose={() => setOpen(false)}
                src={SIGNATURE_FILM.videoUrl}
                poster={SIGNATURE_FILM.poster}
                title={SIGNATURE_FILM.title}
                subtitle={SIGNATURE_FILM.subtitle}
            />
        </>
    );
};
